/**
 * Device Service
 *
 * Centralizes device ID generation and management to avoid duplication
 * and ensure consistency across the application.
 */
import { storageService, StorageKey } from './storage.service';
import { logger } from './logger.service';

class DeviceService {
  private readonly DEVICE_TYPE = 'web';

  /**
   * Gets the device ID from storage or generates a new one if none exists
   * @returns The device ID string
   */
  getDeviceId(): string {
    let deviceId = storageService.get<string>(StorageKey.DEVICE_ID);

    if (!deviceId) {
      deviceId = this.generateDeviceId();
      storageService.set(StorageKey.DEVICE_ID, deviceId);
      logger.debug('Generated new device ID:', deviceId);
    }

    return deviceId;
  }

  /**
   * Generates a new unique device ID
   * @returns A new device ID string
   */
  private generateDeviceId(): string {
    return 'web_' + Math.random().toString(36).substring(2) + '_' + Date.now().toString(36);
  }

  /**
   * Gets the device type
   * @returns The device type string
   */
  getDeviceType(): string {
    return this.DEVICE_TYPE;
  }

  /**
   * Gets the device headers for API requests
   * @returns An object containing the device headers
   */
  getDeviceHeaders(): Record<string, string> {
    return {
      'Device-Id': this.getDeviceId(),
      'Device-Type': this.getDeviceType()
    };
  }

  /**
   * Clears the device ID from storage
   */
  clearDeviceId(): void {
    storageService.remove(StorageKey.DEVICE_ID);
    logger.debug('Device ID cleared');
  }
}

// Export singleton instance
export const deviceService = new DeviceService();

// Also export as default for compatibility with JavaScript imports
export default deviceService;
